// AUD-2 — the SFX patch registry. Resolves any sound id in the project to a synth
// {@link Patch}. Block material voices (`block.<material>.<gesture>`) are generated
// procedurally from a material-timbre × gesture-shape matrix, so every material in
// `data/sounds/groups.json` is covered without enumerating each id; named non-block
// events (mob voices, UI, ambience, weather, music — added across M23) live in the
// explicit `EVENT_PATCHES` map. `patchFor` returns `null` for an id with no patch,
// which the "every sound id has a patch" audit test (M23.1 Verify) asserts never
// happens. PURE — no WebAudio/DOM; import-safe from vitest and the boot host.
import type { Patch, Waveform } from './patch';

// ── Block material voices ────────────────────────────────────────────────────

/** A material's percussive timbre: centre brightness, whether it rings tonally. */
interface Timbre { readonly centerHz: number; readonly tonal: boolean; readonly gain: number; }

const MATERIAL_TIMBRE: Record<string, Timbre> = {
  stone: { centerHz: 1400, tonal: false, gain: 0.5 },
  dirt: { centerHz: 700, tonal: false, gain: 0.42 },
  grass: { centerHz: 1000, tonal: false, gain: 0.38 },
  wood: { centerHz: 500, tonal: true, gain: 0.5 },
  leaves: { centerHz: 3200, tonal: false, gain: 0.3 },
  sand: { centerHz: 1800, tonal: false, gain: 0.4 },
  gravel: { centerHz: 900, tonal: false, gain: 0.46 },
  snow: { centerHz: 2600, tonal: false, gain: 0.28 },
  metal: { centerHz: 2400, tonal: true, gain: 0.5 },
  wool: { centerHz: 600, tonal: false, gain: 0.26 },
  glass: { centerHz: 4200, tonal: true, gain: 0.44 },
  water: { centerHz: 1600, tonal: false, gain: 0.4 },
};

/** The materials with an explicit timbre (unknown families fall back to stone). */
export const SOUND_MATERIALS: readonly string[] = Object.keys(MATERIAL_TIMBRE);

/** A gesture (the `.dig`/`.place`/… tail of a block sound id) shapes the note. */
interface Gesture {
  readonly durationMs: number;
  readonly attackMs: number;
  readonly releaseMs: number;
  readonly brightMul: number;
  readonly gainMul: number;
}

const GESTURES: Record<string, Gesture> = {
  dig: { durationMs: 130, attackMs: 1, releaseMs: 90, brightMul: 1.0, gainMul: 0.8 },
  hit: { durationMs: 120, attackMs: 1, releaseMs: 80, brightMul: 1.0, gainMul: 0.7 },
  break: { durationMs: 260, attackMs: 1, releaseMs: 200, brightMul: 1.2, gainMul: 1.0 },
  place: { durationMs: 150, attackMs: 1, releaseMs: 110, brightMul: 0.8, gainMul: 0.9 },
  step: { durationMs: 90, attackMs: 1, releaseMs: 60, brightMul: 0.9, gainMul: 0.45 },
  fall: { durationMs: 170, attackMs: 1, releaseMs: 120, brightMul: 0.7, gainMul: 0.9 },
  splash: { durationMs: 200, attackMs: 4, releaseMs: 160, brightMul: 1.1, gainMul: 0.7 },
  swim: { durationMs: 240, attackMs: 20, releaseMs: 180, brightMul: 0.9, gainMul: 0.5 },
};

/** Compose the patch for a `block.<material>.<gesture>` id. */
function blockPatch(material: string, gesture: string): Patch {
  const t = MATERIAL_TIMBRE[material] ?? MATERIAL_TIMBRE.stone!;
  const g = GESTURES[gesture] ?? GESTURES.dig!;
  const center = t.centerHz * g.brightMul;
  const wave: Waveform = t.tonal ? 'triangle' : 'noise';
  return {
    wave,
    freq: t.tonal ? center * 0.25 : center, // tonal materials voice a pitch, noise a colour
    durationMs: g.durationMs,
    env: { attackMs: g.attackMs, decayMs: g.durationMs * 0.3, sustain: 0.0, releaseMs: g.releaseMs },
    gain: t.gain * g.gainMul,
    filter: {
      mode: t.tonal ? 'bandpass' : 'lowpass',
      startHz: center * 1.6,
      endHz: center * 0.6,
      q: t.tonal ? 1.4 : 0.7,
    },
    jitterCents: 40,
  };
}

// ── Mob voices (AUD-2) ───────────────────────────────────────────────────────
// Formant-ish per-species timbres (a base pitch + whether the voice is noisy),
// shaped by the action (ambient/hurt/death/step). Any `entity.<species>.<action>`
// id resolves; an unknown species falls back to a neutral grunt.

interface MobTimbre { readonly wave: Waveform; readonly freq: number; readonly gain: number; }

const MOB_TIMBRE: Record<string, MobTimbre> = {
  cow: { wave: 'saw', freq: 160, gain: 0.4 },
  pig: { wave: 'saw', freq: 240, gain: 0.36 },
  sheep: { wave: 'saw', freq: 300, gain: 0.34 },
  chicken: { wave: 'square', freq: 720, gain: 0.28 },
  villager: { wave: 'triangle', freq: 220, gain: 0.34 },
  zombie: { wave: 'saw', freq: 120, gain: 0.4 },
  skeleton: { wave: 'noise', freq: 1, gain: 0.34 },
  creeper: { wave: 'noise', freq: 1, gain: 0.42 },
  spider: { wave: 'noise', freq: 1, gain: 0.3 },
  cat: { wave: 'sine', freq: 620, gain: 0.3 },
  wolf: { wave: 'saw', freq: 300, gain: 0.36 },
  ghast: { wave: 'sine', freq: 300, gain: 0.4 },
  generic: { wave: 'saw', freq: 200, gain: 0.34 },
};
/** Species with a bespoke voice (others use `generic`). */
export const MOB_VOICES: readonly string[] = Object.keys(MOB_TIMBRE);

interface MobAction { readonly durationMs: number; readonly releaseMs: number; readonly gainMul: number; readonly glide: [number, number]; }
const MOB_ACTIONS: Record<string, MobAction> = {
  ambient: { durationMs: 400, releaseMs: 260, gainMul: 0.8, glide: [1.0, 0.92] },
  hurt: { durationMs: 240, releaseMs: 150, gainMul: 1.0, glide: [1.15, 0.9] },
  death: { durationMs: 500, releaseMs: 340, gainMul: 1.0, glide: [1.0, 0.6] },
  step: { durationMs: 90, releaseMs: 60, gainMul: 0.4, glide: [1.0, 1.0] },
};

/** Compose the patch for an `entity.<species>.<action>` id. */
function mobPatch(species: string, action: string): Patch {
  const t = MOB_TIMBRE[species] ?? MOB_TIMBRE.generic!;
  const a = MOB_ACTIONS[action] ?? MOB_ACTIONS.ambient!;
  const noisy = t.wave === 'noise';
  return {
    wave: t.wave,
    freq: t.freq,
    durationMs: a.durationMs,
    env: { attackMs: 3, decayMs: a.durationMs * 0.3, sustain: 0.25, releaseMs: a.releaseMs },
    gain: t.gain * a.gainMul,
    filter: noisy
      ? { mode: 'bandpass', startHz: species === 'skeleton' ? 3200 : 900, endHz: species === 'skeleton' ? 2400 : 500, q: 1.2 }
      : { mode: 'lowpass', startHz: t.freq * 6, endHz: t.freq * 2, q: 0.9 },
    pitchGlide: { from: a.glide[0], to: a.glide[1], glideMs: a.durationMs },
    vibrato: noisy ? undefined : { rateHz: 7, depth: 0.02 },
    jitterCents: 60,
  };
}

// ── Named events (mobs / UI / ambience / weather / music) ────────────────────
// Filled in across the M23 slices; each id here is guaranteed a patch by the audit.

/** Explicit patches for non-block sound events. */
export const EVENT_PATCHES: Record<string, Patch> = {
  // UI (AUD-2): a short bright click and a lower confirm.
  'ui.button.click': {
    wave: 'square', freq: 900, durationMs: 45,
    env: { attackMs: 1, decayMs: 10, sustain: 0.2, releaseMs: 30 }, gain: 0.28,
  },
  'ui.toast.in': {
    wave: 'triangle', freq: 660, durationMs: 90,
    env: { attackMs: 2, decayMs: 30, sustain: 0.3, releaseMs: 55 }, gain: 0.3,
    pitchGlide: { from: 0.9, to: 1.15, glideMs: 90 },
  },
  // Player actions that aren't tied to a block material.
  'player.levelup': {
    wave: 'sine', freq: 523.25, durationMs: 600,
    env: { attackMs: 4, decayMs: 120, sustain: 0.5, releaseMs: 300 }, gain: 0.34,
    pitchGlide: { from: 1.0, to: 1.5, glideMs: 260 }, detuneCents: 702,
  },
  'player.hurt': {
    wave: 'saw', freq: 220, durationMs: 200,
    env: { attackMs: 1, decayMs: 60, sustain: 0.2, releaseMs: 120 }, gain: 0.36,
    filter: { mode: 'lowpass', startHz: 1400, endHz: 500, q: 0.9 }, pitchGlide: { from: 1.1, to: 0.85, glideMs: 200 },
  },
  'item.eat': {
    wave: 'triangle', freq: 300, durationMs: 130,
    env: { attackMs: 4, decayMs: 40, sustain: 0.3, releaseMs: 80 }, gain: 0.3,
    filter: { mode: 'lowpass', startHz: 900, endHz: 400, q: 0.8 }, jitterCents: 120,
  },
  'entity.experience_orb.pickup': {
    wave: 'sine', freq: 900, durationMs: 90,
    env: { attackMs: 1, decayMs: 20, sustain: 0.2, releaseMs: 60 }, gain: 0.28,
    pitchGlide: { from: 1.0, to: 1.4, glideMs: 90 }, jitterCents: 80,
  },
  'random.pop': {
    wave: 'sine', freq: 600, durationMs: 60,
    env: { attackMs: 1, decayMs: 12, sustain: 0.1, releaseMs: 40 }, gain: 0.24, pitchGlide: { from: 1.2, to: 1.0, glideMs: 40 },
  },
  // Ambience beds (AUD-2): long, quiet, low-pitched pads/rumbles.
  'ambient.cave': {
    wave: 'sine', freq: 90, durationMs: 2400,
    env: { attackMs: 600, decayMs: 400, sustain: 0.5, releaseMs: 900 }, gain: 0.16,
    filter: { mode: 'lowpass', startHz: 300, endHz: 180, q: 0.7 }, detuneCents: 12,
  },
  'ambient.nether': {
    wave: 'saw', freq: 60, durationMs: 2600,
    env: { attackMs: 500, decayMs: 500, sustain: 0.55, releaseMs: 1000 }, gain: 0.18,
    filter: { mode: 'lowpass', startHz: 220, endHz: 140, q: 0.8 }, vibrato: { rateHz: 0.4, depth: 0.05 },
  },
  'ambient.underwater': {
    wave: 'sine', freq: 140, durationMs: 2200,
    env: { attackMs: 500, decayMs: 400, sustain: 0.5, releaseMs: 900 }, gain: 0.16,
    filter: { mode: 'lowpass', startHz: 500, endHz: 260, q: 0.7 },
  },
  // Weather (AUD-2).
  'weather.rain': {
    wave: 'noise', freq: 1, durationMs: 1800,
    env: { attackMs: 400, decayMs: 300, sustain: 0.55, releaseMs: 700 }, gain: 0.14,
    filter: { mode: 'highpass', startHz: 2200, endHz: 2000, q: 0.7 },
  },
  'weather.thunder': {
    wave: 'noise', freq: 1, durationMs: 900,
    env: { attackMs: 1, decayMs: 300, sustain: 0.3, releaseMs: 500 }, gain: 0.5,
    filter: { mode: 'lowpass', startHz: 400, endHz: 120, q: 0.9 },
  },
  'block.portal.travel': {
    wave: 'sine', freq: 220, durationMs: 800,
    env: { attackMs: 30, decayMs: 200, sustain: 0.5, releaseMs: 400 }, gain: 0.24,
    vibrato: { rateHz: 8, depth: 0.06 }, detuneCents: 40,
  },
};

/**
 * Resolve a sound id to its patch, or `null` if none is registered. Block ids of
 * the form `block.<material>.<gesture>` are generated; everything else must be in
 * {@link EVENT_PATCHES}. The audit asserts this is total over the declared id set.
 */
export function patchFor(id: string): Patch | null {
  const explicit = EVENT_PATCHES[id];
  if (explicit) return explicit;
  const parts = id.split('.');
  if (parts[0] === 'block' && parts.length >= 3) {
    return blockPatch(parts[1]!, parts[parts.length - 1]!);
  }
  if (parts[0] === 'entity' && parts.length >= 3) {
    return mobPatch(parts[1]!, parts[parts.length - 1]!);
  }
  return null;
}

/** Runtime resolver: never silent — unknown ids fall back to a stone dig thud. */
export function patchForOrDefault(id: string): Patch {
  return patchFor(id) ?? blockPatch('stone', 'dig');
}
